/**
 * `/find-a-partner` — Find Your Local Water Expert.
 *
 * Public suburb/postcode search that routes visitors to the correct
 * partner storefront. Falls back gracefully to national mail-out
 * when no active partner matches the query — never dead-ends.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, ArrowRight, MapPin, Send, ShieldCheck, Loader2 } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { FindPartnerContent, FIND_PARTNER_FAQS } from "@/components/FindPartnerContent";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";

const BASE_URL = SEO_DEFAULTS.baseUrl;

export default function FindPartner() {
  const [sp, setSp] = useSearchParams();
  const initialQ = sp.get("q") || "";
  const [q, setQ] = useState(initialQ);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useSEO({
    title: "Find a Local Water Testing Partner | AWARE",
    description:
      "Find an AWARE water testing partner near you where available, or book a mail-out tank or bore water test kit anywhere in Australia.",
    url: `${BASE_URL}/find-a-partner`,
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FIND_PARTNER_FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  });

  // Auto-fire the search when navigating in with ?q=… (Google Ads / QR).
  useEffect(() => {
    if (initialQ && !searched) {
      runSearch(initialQ);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQ]);

  async function runSearch(query) {
    if (!query || !query.trim()) return;
    setLoading(true);
    setSearched(true);
    setSp({ q: query }, { replace: true });
    try {
      const { data } = await api.get(`/find-partner?q=${encodeURIComponent(query)}`);
      setResult(data);
    } catch {
      setResult(null);
    } finally {
      setLoading(false);
    }
  }

  const submit = (e) => {
    e.preventDefault();
    runSearch(q);
  };

  const match = result?.match || null;

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="find-partner-page">
      <Header />

      {/* Hero + search */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-900 to-cyan-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-16 md:py-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-cyan-500/15 border border-cyan-400/30 text-cyan-200 px-3 py-1 text-xs font-semibold mb-4">
            <ShieldCheck className="w-3.5 h-3.5" /> Local · Independent · Australian laboratory
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="find-partner-h1">
            Find Your Local Water Expert
          </h1>
          <p className="mt-4 text-lg text-slate-200 max-w-2xl">
            Enter your suburb or postcode. If we have a PWS Partner in your
            area they&#39;ll handle everything locally. If not, we&#39;ll ship a
            sample kit anywhere in Australia.
          </p>

          <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-2xl" data-testid="find-partner-form">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="e.g. Doonan or 4562"
                autoFocus
                className="w-full h-14 pl-12 pr-4 rounded-full bg-white text-slate-900 text-base focus:outline-none focus:ring-4 focus:ring-cyan-500/40"
                data-testid="find-partner-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading || !q.trim()}
              className="rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 h-14 px-8 font-bold"
              data-testid="find-partner-submit"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Searching…</> : <>Find my Partner <ArrowRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </form>
        </div>
      </section>

      {/* Result */}
      <section className="border-b border-slate-100 py-12">
        <div className="max-w-4xl mx-auto px-6">
          {!searched && !loading && (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center text-slate-600" data-testid="find-partner-idle">
              Type your suburb or postcode above to see who covers your area.
            </div>
          )}

          {searched && !loading && match && (
            <PartnerMatchCard match={match} query={result?.query} />
          )}

          {searched && !loading && !match && result && (
            <NoMatchFallback query={result?.query} fallback={result?.fallback} />
          )}
        </div>
      </section>

      {/* Sep 2026 — route-specific content blocks (Jason thin_content:
          the page was 223 words). Australia-wide framing, "where
          available" hedging, no partner guarantees. */}
      <FindPartnerContent />

      <Footer />
    </div>
  );
}

function PartnerMatchCard({ match, query }) {
  return (
    <div className="rounded-3xl overflow-hidden border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white shadow-sm" data-testid="find-partner-match">
      <div className="p-6 md:p-8 flex flex-col sm:flex-row gap-6 items-start">
        {(match.franchisee_photo_url || match.franchisee_logo_url) && (
          <img
            src={match.franchisee_photo_url || match.franchisee_logo_url}
            alt={match.business_name}
            className="w-24 h-24 md:w-32 md:h-32 rounded-2xl object-cover ring-4 ring-emerald-100 shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-900 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1">
            <ShieldCheck className="w-3 h-3" /> Your Local Water Expert
          </div>
          <h2 className="mt-3 text-3xl font-bold text-slate-900" data-testid="find-partner-match-name">{match.business_name}</h2>
          {match.region_label && (
            <div className="mt-1 text-sm text-slate-600 flex items-center gap-1.5">
              <MapPin className="w-4 h-4" /> {match.region_label}
            </div>
          )}
          <p className="mt-4 text-slate-700">
            Match found for <span className="font-semibold">&ldquo;{query}&rdquo;</span> {match.matched_by === "postcode" ? "on postcode" : "on suburb"} — <span className="font-semibold">{match.business_name}</span> handles this area end-to-end.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to={match.book_href}>
              <Button className="rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold h-12 px-6" data-testid="find-partner-book">
                Book Water Test <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
            <Link to={match.profile_href}>
              <Button variant="outline" className="rounded-full border-slate-300 h-12 px-6" data-testid="find-partner-profile">
                View partner storefront
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function NoMatchFallback({ query, fallback }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8" data-testid="find-partner-fallback">
      <div className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 text-slate-700 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1">
        <Send className="w-3 h-3" /> National mail-out
      </div>
      <h2 className="mt-3 text-2xl md:text-3xl font-bold text-slate-900">
        No local partner in <span className="text-cyan-700">{query}</span> yet — mail-out has you covered.
      </h2>
      <p className="mt-3 text-slate-700 max-w-2xl">
        We ship a pre-paid sample kit anywhere in Australia. You take the
        sample, drop the return satchel in a post box, and we email your
        laboratory report the moment it&#39;s ready.
      </p>
      <div className="mt-6 flex flex-wrap gap-3">
        <Link to={fallback?.book_href || "/book-water-test"}>
          <Button className="rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold h-12 px-6" data-testid="find-partner-fallback-book">
            {fallback?.label || "Book with AWARE"} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
        <Link to="/mail-out-water-testing-kits">
          <Button variant="outline" className="rounded-full border-slate-300 h-12 px-6" data-testid="find-partner-fallback-mailout">
            How mail-out works
          </Button>
        </Link>
      </div>
    </div>
  );
}
